import { css, cx } from "@emotion/css";
import { FileText, Plus } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";
import routing from "routing";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import type { FolderItemType } from "src/components/Folders/types";
import { FolderItemSkeleton } from "src/components/NewComponents/Skeletons/FolderItemSkeleton";
import formatHtmlTag from "src/helpers/formatHtmlTag";
import { useSearchUserNotesInfinite } from "src/hooks/useFolders";

interface ModalSelectedItem {
	itemId: string;
	itemType: FolderItemType;
}

interface NoteItem {
	id: string;
	name?: string;
	note?: string;
	createdAt?: string;
	[key: string]: unknown;
}

interface NotesFolderTabProps {
	debouncedSearchValue: string;
	selectedItems: ModalSelectedItem[];
	toggleSelection: (item: NoteItem, type: FolderItemType) => void;
	onClose: () => void;
	setIsSearching: (value: boolean) => void;
	folderId: string;
}

export const NotesFolderTab = ({
	debouncedSearchValue,
	selectedItems,
	toggleSelection,
	onClose,
	setIsSearching,
	folderId,
}: NotesFolderTabProps) => {
	const { t } = useTranslation();
	const createNoteUrl = generatePath(routing.paths.createNote, {
		id: folderId,
	});

	const {
		data: notesData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isFetching,
		isLoading,
	} = useSearchUserNotesInfinite(
		{
			...(debouncedSearchValue ? { search: debouncedSearchValue } : {}),
		},
		true,
	);

	useEffect(() => {
		setIsSearching(isFetching && !isLoading);
	}, [isFetching, isLoading, setIsSearching]);

	const flatNotes = useMemo(() => {
		if (!notesData?.pages) return [];
		return notesData.pages.flatMap(
			(page) => (page.data || []) as unknown as NoteItem[],
		);
	}, [notesData?.pages]);

	const renderNoteItem = (item: NoteItem) => {
		const isSelected = selectedItems.some(
			(i) => i.itemId === item.id && i.itemType === "note",
		);

		const preview = item.note ? formatHtmlTag(item.note) : "";
		const formattedDate = item.createdAt
			? new Date(item.createdAt).toLocaleDateString("pl-PL")
			: null;

		return (
			<div
				key={item.id as string}
				data-test-id="folder-modal-note-item"
				data-note-id={item.id}
				onClick={() => toggleSelection(item, "note")}
				className={cx("cursor-pointer position-relative transition-all")}
			>
				<div
					className={cx(
						"pointer-events-none border-2 border-solid border-radius-16 radius-16",
					)}
				>
					<div
						className={cx(
							"content-box flex flex-col gap-16 justify-between mb-0 w-100 mx-0",
							css`min-height: 150px;`,
							isSelected ? "border-color-primary" : "",
						)}
					>
						<div className="flex flex-col gap-16">
							{formattedDate && (
								<div className="note-list-item">{formattedDate}</div>
							)}
							<div className="flex items-center gap-8">
								<FileText className="icon-small" />
								<Tooltip title={item.name}>
									<h3 className="text-ellipsis-1 m-0 size-16">
										{item.name || t("note.untitled", "Notatka bez tytułu")}
									</h3>
								</Tooltip>
							</div>
							<p className="text-ellipsis-2 m-0 size-14 text-black-80">
								{preview || "\u00A0"}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	};

	if (isLoading && !flatNotes.length) {
		return (
			<div className="folder-modal-scroll">
				<div className="folder-modal-grid">
					{Array.from({ length: 4 }).map((_, i) => (
						<FolderItemSkeleton key={i} />
					))}
				</div>
			</div>
		);
	}

	if (!flatNotes.length) {
		return (
			<div className="flex flex-col items-center justify-center py-48 gap-16">
				<span className="text-black-60 font-14">
					{debouncedSearchValue
						? t("folder.noSearchResults", "Brak wyników wyszukiwania")
						: t("folder.noNotes", "Nie masz jeszcze żadnych notatek")}
				</span>
				{!debouncedSearchValue && (
					<PrimaryButton
						component={Link}
						to={createNoteUrl}
						onClick={onClose}
						className="flex items-center gap-8"
					>
						<Plus className="icon-small" />
						{t("folder.createNote", "Utwórz notatkę")}
					</PrimaryButton>
				)}
			</div>
		);
	}

	return (
		<div className="folder-modal-scroll">
			<div className="folder-modal-grid">{flatNotes.map(renderNoteItem)}</div>
			{hasNextPage && (
				<div className="flex justify-center mt-24">
					<TextPrimaryButton
						onClick={() => fetchNextPage()}
						disabled={isFetchingNextPage}
					>
						{isFetchingNextPage
							? t("folder.loading", "Ładowanie...")
							: t("folder.loadMore", "Załaduj więcej")}
					</TextPrimaryButton>
				</div>
			)}
		</div>
	);
};
